<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { commissionSkyDashboardApi } from 'dashboard/api/commissionsSky';

const store = useStore();
const currentUser = computed(() => store.getters.getCurrentUser);

const loading = ref(true);
const data = ref(null);
const competence = ref(new Date().toISOString().slice(0, 7)); // YYYY-MM

const competenceOptions = computed(() => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i += 1) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(
      `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
    );
  }
  return out;
});

const MESES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const formatCents = c =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    (parseInt(c, 10) || 0) / 100
  );

const monthLabel = c => {
  const [y, m] = c.split('-');
  return `${MESES[parseInt(m, 10) - 1]}/${y}`;
};

const formatDate = d => {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

const METHOD_LABEL = { pix: 'PIX', credit_card: 'Cartão', boleto: 'Boleto' };
const methodLabel = m => METHOD_LABEL[m] || m || '—';
const methodClass = m =>
  ({
    pix: 'bg-emerald-500/15 text-emerald-600',
    credit_card: 'bg-blue-500/15 text-blue-600',
    boleto: 'bg-amber-500/15 text-amber-600',
  })[m] || 'bg-n-alpha-2 text-n-slate-11';

const nextPayout = computed(() => data.value?.next_payout || {});
const eligible = computed(() => data.value?.eligible || { items: [] });
const pending = computed(() => data.value?.pending || { items: [] });
const goal = computed(() => data.value?.goal || {});
const history = computed(() => data.value?.history || []);

const goalPct = computed(() => Math.min(parseFloat(goal.value.pct) || 0, 100));

function goalBarColor(pct) {
  if (pct >= 100) return '#22c55e';
  if (pct >= 80) return '#3b82f6';
  return '#f59e0b';
}

const payoutStatusClass = s =>
  ({
    paid: 'bg-emerald-500/15 text-emerald-600',
    open: 'bg-blue-500/15 text-blue-600',
    reopened: 'bg-amber-500/15 text-amber-600',
  })[s] || 'bg-n-alpha-2 text-n-slate-11';
const payoutStatusLabel = s =>
  ({ paid: 'Pago', open: 'Em aberto', reopened: 'Reaberto' })[s] || s;

async function fetchDashboard() {
  loading.value = true;
  try {
    const res = await commissionSkyDashboardApi.show({
      competence: competence.value,
    });
    data.value = res.data;
  } catch {
    data.value = null;
  } finally {
    loading.value = false;
  }
}

onMounted(fetchDashboard);
</script>

<template>
  <div class="flex flex-col h-full overflow-auto bg-n-background">
    <div class="mx-auto w-full max-w-5xl p-4 sm:p-6 space-y-5">
      <!-- Header -->
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-xl font-bold text-n-slate-12">
            <span class="i-lucide-wallet text-n-brand" />
            Minhas Comissões
          </h1>
          <p class="mt-0.5 text-sm text-n-slate-10">
            {{ currentUser.name }} · repasse toda sexta, conforme a entrada de
            caixa
          </p>
        </div>
        <select
          v-model="competence"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          @change="fetchDashboard"
        >
          <option v-for="c in competenceOptions" :key="c" :value="c">
            {{ monthLabel(c) }}
          </option>
        </select>
      </div>

      <div
        v-if="loading"
        class="flex items-center justify-center py-20 text-n-slate-10"
      >
        <span class="i-lucide-loader-2 animate-spin text-2xl" />
      </div>

      <template v-else-if="data">
        <!-- Hero: próximo repasse -->
        <div
          class="rounded-2xl border border-n-weak/40 bg-gradient-to-br from-emerald-500/10 to-n-alpha-1 p-5"
        >
          <div class="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p
                class="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-n-slate-10"
              >
                <span class="i-lucide-calendar-check text-emerald-500" />
                Próximo repasse · sexta {{ formatDate(nextPayout.date) }}
              </p>
              <p class="mt-1 text-4xl font-extrabold text-emerald-600">
                {{ formatCents(nextPayout.amount_cents) }}
              </p>
              <p class="mt-0.5 text-xs text-n-slate-10">
                {{ nextPayout.count || 0 }} parcela(s) já em caixa liberam nesta
                sexta
              </p>
            </div>
            <span class="i-lucide-banknote text-5xl text-emerald-500/40" />
          </div>
        </div>

        <!-- Cartões -->
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3">
            <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
              Já em caixa (a liberar)
            </p>
            <p class="mt-1 text-lg font-bold text-emerald-600">
              {{ formatCents(eligible.amount_cents) }}
            </p>
            <p class="text-[11px] text-n-slate-9">
              {{ eligible.count || 0 }} parcela(s)
            </p>
          </div>
          <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3">
            <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
              Aguardando caixa
            </p>
            <p class="mt-1 text-lg font-bold text-amber-600">
              {{ formatCents(pending.amount_cents) }}
            </p>
            <p class="text-[11px] text-n-slate-9">
              {{ pending.count || 0 }} parcela(s)
            </p>
          </div>
          <div
            class="col-span-2 rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3 sm:col-span-1"
          >
            <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
              Meta do mês
            </p>
            <p class="mt-1 text-lg font-bold text-n-slate-12">
              {{ formatCents(goal.realized_cents) }}
              <span class="text-xs font-normal text-n-slate-10"
                >/ {{ formatCents(goal.target_cents) }}</span
              >
            </p>
            <div
              v-if="goal.target_cents"
              class="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200"
            >
              <div
                class="h-full rounded-full transition-all duration-500"
                :style="{
                  width: goalPct + '%',
                  background: goalBarColor(goalPct),
                }"
              />
            </div>
          </div>
        </div>

        <!-- Já em caixa (elegível) -->
        <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-5">
          <h3
            class="mb-3 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-check-circle text-emerald-500" />
            Já em caixa — liberam por sexta
          </h3>
          <div
            v-if="eligible.items && eligible.items.length"
            class="space-y-1.5 max-h-64 overflow-auto"
          >
            <div
              v-for="i in eligible.items"
              :key="i.id"
              class="flex items-center justify-between gap-2 rounded-lg border border-n-weak/30 px-3 py-2 text-sm"
            >
              <span class="flex min-w-0 items-center gap-2">
                <span
                  class="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                  :class="methodClass(i.method)"
                  >{{ methodLabel(i.method) }}</span
                >
                <span class="truncate text-n-slate-11">{{
                  i.contact_name || 'Cliente'
                }}</span>
              </span>
              <span class="flex shrink-0 items-center gap-3">
                <span class="text-[11px] text-n-slate-9"
                  >sexta {{ formatDate(i.release_date) }}</span
                >
                <span class="font-semibold text-emerald-600">{{
                  formatCents(i.commission_amount_cents)
                }}</span>
              </span>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            Nada em caixa aguardando repasse.
          </p>
        </div>

        <!-- Aguardando entrada de caixa -->
        <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-5">
          <h3
            class="mb-3 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-hourglass text-amber-500" />
            Aguardando entrada de caixa
          </h3>
          <div
            v-if="pending.items && pending.items.length"
            class="space-y-1.5 max-h-64 overflow-auto"
          >
            <div
              v-for="i in pending.items"
              :key="i.id"
              class="flex items-center justify-between gap-2 rounded-lg border border-n-weak/30 px-3 py-2 text-sm"
            >
              <span class="flex min-w-0 items-center gap-2">
                <span
                  class="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                  :class="methodClass(i.method)"
                  >{{ methodLabel(i.method) }}</span
                >
                <span class="truncate text-n-slate-11">{{
                  i.contact_name || 'Cliente'
                }}</span>
              </span>
              <span class="font-semibold text-amber-600">{{
                formatCents(i.commission_amount_cents)
              }}</span>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            Nenhuma venda aguardando pagamento do cliente.
          </p>
        </div>

        <!-- Histórico de repasses -->
        <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-5">
          <h3
            class="mb-3 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-history text-n-brand" />
            Histórico de repasses
          </h3>
          <div v-if="history.length" class="space-y-1.5 max-h-64 overflow-auto">
            <div
              v-for="p in history"
              :key="p.id"
              class="flex items-center justify-between gap-2 text-sm"
            >
              <span class="text-n-slate-11"
                >sexta {{ formatDate(p.payout_date) }}</span
              >
              <span class="flex items-center gap-2">
                <span
                  class="rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
                  :class="payoutStatusClass(p.status)"
                  >{{ payoutStatusLabel(p.status) }}</span
                >
                <span class="font-semibold text-n-slate-12">{{
                  formatCents(p.total_amount_cents)
                }}</span>
              </span>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">Sem repasses ainda.</p>
        </div>
      </template>

      <div v-else class="py-20 text-center text-n-slate-10">
        Não foi possível carregar suas comissões.
      </div>
    </div>
  </div>
</template>
